import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import videojs from 'video.js/dist/video.js'
import CustomVideoControls from './CustomVideoControls'
import { startSession, endSession } from '../services/usageTracking'
import './LiveStreamView.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

function getIsPortrait() {
  if (typeof window === 'undefined') {
    return false
  }
  return window.innerHeight > window.innerWidth
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--'
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

const DEFAULT_STREAM = {
  id: null,
  name: 'Live stream',
  stream_16_9_url: null,
  stream_9_16_url: null,
  backup_stream_16_9_url: null,
  backup_stream_9_16_url: null,
  image_16_9: null,
  image_9_16: null
}

const LiveStreamView = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const playerRef = useRef(null)
  const videoNodeRef = useRef(null)
  const hideControlsTimeoutRef = useRef(null)
  const [stream, setStream] = useState(DEFAULT_STREAM)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPortrait, setIsPortrait] = useState(getIsPortrait())
  const [isPlaying, setIsPlaying] = useState(true) // Autoplay este activ
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(NaN)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [overlayVisible, setOverlayVisible] = useState(true)
  const [airPlayAvailable, setAirPlayAvailable] = useState(false)
  const usageSessionIdRef = useRef(null) // ID-ul sesiunii de tracking
  const playStartTimeRef = useRef(null) // Timpul când a început play-ul

  const isLive = useMemo(() => !Number.isFinite(duration) || duration === 0, [duration])
  const progressPercent = useMemo(() => {
    if (isLive || duration <= 0) return 0
    return Math.max(0, Math.min(100, (currentTime / duration) * 100))
  }, [currentTime, duration, isLive])
  const volumePercent = useMemo(() => Math.max(0, Math.min(100, volume * 100)), [volume])
  const volumeIcon = useMemo(() => {
    if (isMuted || volumePercent === 0) {
      return 'muted'
    }
    if (volumePercent < 50) {
      return 'low'
    }
    return 'high'
  }, [isMuted, volumePercent])

  useEffect(() => {
    if (!id) {
      navigate('/v3.0')
    }
  }, [id, navigate])

  useEffect(() => {
    const updateOrientation = () => {
      setIsPortrait(getIsPortrait())
    }

    updateOrientation()
    window.addEventListener('resize', updateOrientation)
    window.addEventListener('orientationchange', updateOrientation)

    return () => {
      window.removeEventListener('resize', updateOrientation)
      window.removeEventListener('orientationchange', updateOrientation)
    }
  }, [])

  useEffect(() => {
    const stateStream = location.state?.stream
    if (stateStream) {
      setStream({ ...DEFAULT_STREAM, ...stateStream })
      setLoading(false)
      return
    }

    async function loadStream() {
      if (!id) return
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/live-streams/${id}`)
        if (!response.ok) {
          throw new Error(`Nu am putut încărca fluxul. Cod: ${response.status}`)
        }
        const json = await response.json()
        if (!json.success || !json.data) {
          throw new Error('Răspuns invalid de la server.')
        }
        setStream({ ...DEFAULT_STREAM, ...json.data })
      } catch (err) {
        console.error('loadStream failed', err)
        setError(err.message || 'Nu am putut încărca fluxul selectat.')
      } finally {
        setLoading(false)
      }
    }

    loadStream()
  }, [id, location.state])

  const selectSource = useCallback((targetStream) => {
    const primary = isPortrait ? targetStream.stream_9_16_url : targetStream.stream_16_9_url
    const backup = isPortrait ? targetStream.backup_stream_9_16_url : targetStream.backup_stream_16_9_url

    if (primary) {
      return { src: primary, type: 'application/x-mpegURL' }
    }
    if (backup) {
      return { src: backup, type: 'application/x-mpegURL' }
    }
    return null
  }, [isPortrait])

  const poster = useMemo(() => {
    if (isPortrait) {
      return stream.image_9_16 || stream.image_16_9
    }
    return stream.image_16_9 || stream.image_9_16
  }, [isPortrait, stream])

  const ensureControlsVisible = useCallback(() => {
    setControlsVisible(true)
    setOverlayVisible(true)
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current)
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false)
      setOverlayVisible(false)
    }, 5000)
  }, [])

  useEffect(() => {
    if (!videoNodeRef.current) {
      console.warn('[LiveStreamView] videoNodeRef este null')
      return
    }

    const source = selectSource(stream)
    if (!source) {
      console.warn('[LiveStreamView] Nu am găsit sursă pentru stream', stream)
      return
    }


    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    if (!playerRef.current) {
      const playerOpts = {
        autoplay: true,
        controls: false,
        muted: false,
        preload: 'auto',
        html5: {
          nativeAudioTracks: false,
          nativeVideoTracks: false
        },
        liveui: false,
        inactivityTimeout: 0,
        fluid: false,
        responsive: true
      }

      if (!isSafari) {
        playerOpts.html5.vhs = {
          overrideNative: true,
          enableLowInitialPlaylist: true,
          limitRenditionByPlayerDimensions: true,
          smoothQualityChange: true,
          bandwidth: 5000000,
          backBufferLength: 30,
          bufferBasedABR: true,
          experimentalBufferBasedABR: true,
          experimentalLeastPixelDiffSelector: true,
          maxPlaylistRetries: 5,
          maxPlaylistRetryDelay: 10000,
          maxSegmentRetries: 3,
          maxSegmentRetryDelay: 5000,
          maxBufferLength: 30,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          liveTolerance: 15,
          withCredentials: false,
          liveTracker: {
            trackingThreshold: 0,
            liveTolerance: 15
          }
        }
      }

      playerRef.current = videojs(videoNodeRef.current, playerOpts)

      const player = playerRef.current

      const updateMetrics = () => {
        setIsPlaying(!player.paused())
        setIsMuted(player.muted())
        const currentVolume = player.volume()
        setVolume(currentVolume)
        const dur = player.duration()
        setDuration(dur)
        setCurrentTime(player.currentTime())
      }

      player.ready(() => {
        player.src(source)
        const startPlayback = () => {
          player.play().then(() => {
            // Playback started
          }).catch((err) => {
            console.warn('[LiveStreamView] play() a eșuat', err)
          })
        }

        if (isSafari) {
          const videoEl = player.el()?.querySelector('video')
          if (videoEl) {
            videoEl.src = source.src
            videoEl.addEventListener('loadedmetadata', () => {
              startPlayback()
            }, { once: true })
          } else {
            startPlayback()
          }
        } else {
          startPlayback()
        }

        const videoEl = player.el()?.querySelector('video')
        if (videoEl && typeof videoEl.webkitShowPlaybackTargetPicker === 'function') {
          setAirPlayAvailable(true)
        }
        updateMetrics()
        ensureControlsVisible()
      })

      // Event listeners pentru play/pause
      player.on('play', () => {
        setIsPlaying(true)
        // Începem tracking-ul când începe play-ul
        if (!usageSessionIdRef.current && stream.id) {
          usageSessionIdRef.current = startSession('live_stream', stream.id, {
            stream_name: stream.name || ''
          })
          playStartTimeRef.current = Date.now()
        }
      })

      player.on('pause', () => {
        setIsPlaying(false)
        // Finalizăm tracking-ul dacă există
        if (usageSessionIdRef.current && playStartTimeRef.current) {
          endSession(usageSessionIdRef.current)
          usageSessionIdRef.current = null
          playStartTimeRef.current = null
        }
      })

      player.on('error', () => {
        const error = player.error()
        console.error('[LiveStreamView] Player error', error)
        const fallback = selectSource({
          stream_16_9_url: stream.backup_stream_16_9_url,
          stream_9_16_url: stream.backup_stream_9_16_url
        })
        if (fallback) {
          if (isSafari) {
            const videoEl = player.el()?.querySelector('video')
            if (videoEl) {
              videoEl.src = fallback.src
              videoEl.play().catch((err) => console.warn('[LiveStreamView] play() fallback safari a eșuat', err))
            }
          }
          player.src(fallback)
          player.play().catch((err) => console.warn('[LiveStreamView] play() fallback a eșuat', err))
        }
      })
    } else {
      const player = playerRef.current
      player.src(source)
      if (isSafari) {
        const videoEl = player.el()?.querySelector('video')
        if (videoEl) {
          videoEl.src = source.src
          videoEl.play().catch((err) => console.warn('[LiveStreamView] play() safari existent a eșuat', err))
        }
      }
      player.play().catch((err) => console.warn('[LiveStreamView] play() pe player existent a eșuat', err))
    }

    playerRef.current.poster(poster || '')

    return () => {
      // Cleanup: finalizăm sesiunea de tracking dacă există
      if (usageSessionIdRef.current) {
        endSession(usageSessionIdRef.current)
        usageSessionIdRef.current = null
        playStartTimeRef.current = null
      }
    }
  }, [selectSource, stream, poster, ensureControlsVisible])

  useEffect(() => {
    const player = playerRef.current
    return () => {
      if (player) {
        player.dispose()
        playerRef.current = null
      }
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
      }
    }
  }, [])

  const rootClassName = useMemo(() => {
    return `live-view-root${isPortrait ? ' live-view-root--portrait' : ''}`
  }, [isPortrait])

  const canSeek = !isLive && Number.isFinite(duration) && duration > 0

  const handleTogglePlay = () => {
    const player = playerRef.current
    if (!player) return
    if (player.paused()) {
      player.play().then(() => {
        setIsPlaying(true)
      }).catch(() => {})
    } else {
      player.pause()
      setIsPlaying(false)
    }
  }

  const handleToggleMute = () => {
    const player = playerRef.current
    if (!player) return
    player.muted(!player.muted())
    if (!player.muted() && player.volume() === 0) {
      player.volume(0.3)
    }
  }

  const handleVolumeChange = (event) => {
    const player = playerRef.current
    if (!player) return
    const nextVolume = Number(event.target.value)
    setVolume(nextVolume)
    player.volume(nextVolume)
    if (nextVolume > 0 && player.muted()) {
      player.muted(false)
      setIsMuted(false)
    }
    if (nextVolume === 0 && !player.muted()) {
      player.muted(false)
      setIsMuted(false)
    }
  }

  const handleSeek = (event) => {
    const player = playerRef.current
    if (!player || !canSeek) return
    const nextTime = Number(event.target.value)
    player.currentTime(nextTime)
  }

  const handleToggleFullscreen = () => {
    const player = playerRef.current
    if (!player) return
    if (player.isFullscreen()) {
      player.exitFullscreen()
    } else {
      player.requestFullscreen()
    }
  }

  const handleAirPlay = () => {
    const videoEl = videoNodeRef.current
    if (videoEl && typeof videoEl.webkitShowPlaybackTargetPicker === 'function') {
      try {
        videoEl.webkitShowPlaybackTargetPicker()
      } catch (err) {
        console.warn('AirPlay failed', err)
      }
    }
  }

  const handleMouseMove = () => {
    ensureControlsVisible()
  }

  return (
    <div className={rootClassName} onMouseMove={handleMouseMove} onTouchStart={handleMouseMove}>
      <div className={`live-view-overlay ${overlayVisible ? 'live-view-overlay--visible' : ''}`}>
        <button type="button" className="live-view-back" onClick={() => navigate(-1)}>
          ← Înapoi
        </button>
        <h1 className="live-view-title">{stream.name}</h1>
      </div>

      {loading && <div className="live-view-status">Se încarcă fluxul...</div>}
      {error && <div className="live-view-status live-view-status-error">{error}</div>}

      {!loading && !error && stream && (
        <div className={`live-view-player ${controlsVisible ? 'live-view-player--active' : ''}`}>
          <div className="live-view-player-inner">
            <video
              ref={videoNodeRef}
              className="video-js vjs-big-play-centered"
              playsInline
              poster={poster || undefined}
            />
          </div>

          {controlsVisible && (
            <CustomVideoControls
              isPlaying={isPlaying}
              isMuted={isMuted}
              volume={volume}
              currentTime={currentTime}
              duration={duration}
              isFullscreen={isFullscreen}
              streamName={stream.name}
              description={null}
              onPlayPause={handleTogglePlay}
              onMuteToggle={handleToggleMute}
              onVolumeChange={(newVolume) => {
                const player = playerRef.current
                if (player) {
                  player.volume(newVolume)
                  setVolume(newVolume)
                  if (newVolume === 0) {
                    player.muted(true)
                    setIsMuted(true)
                  } else if (isMuted) {
                    player.muted(false)
                    setIsMuted(false)
                  }
                }
              }}
              onSeek={(newTime) => {
                const player = playerRef.current
                if (player && canSeek) {
                  player.currentTime(newTime)
                }
              }}
              onFullscreenToggle={handleToggleFullscreen}
              onAirplayToggle={handleAirPlay}
              onBack={() => navigate(-1)}
              onTitleHoverChange={() => {}}
            />
          )}

          <div className={`live-view-controls live-view-controls-hidden ${controlsVisible ? 'live-view-controls--visible' : ''}`}>
            <div className="live-view-controls-left">
              <button type="button" className="live-view-control-btn" onClick={handleTogglePlay}>
                {isPlaying ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              <button type="button" className="live-view-control-btn" onClick={handleToggleMute}>
                {volumeIcon === 'muted' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
                {volumeIcon === 'low' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
                {volumeIcon === 'high' && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>

              <div className="live-view-volume">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  onInput={handleVolumeChange}
                  style={{ '--progress': `${volumePercent}%` }}
                />
              </div>
            </div>

            <div className="live-view-controls-center">
              {canSeek ? (
                <div className="live-view-timeline">
                  <span>{formatTime(currentTime)}</span>
                  <input
                    type="range"
                    min="0"
                    max={Number.isFinite(duration) ? duration : 0}
                    step="0.1"
                    value={Math.min(currentTime, duration || 0)}
                    onChange={handleSeek}
                    onInput={handleSeek}
                    style={{ '--progress': `${progressPercent}%` }}
                  />
                  <span>{formatTime(duration)}</span>
                </div>
              ) : (
                <div className="live-view-live-indicator">
                  <span className="live-dot" />LIVE
                </div>
              )}
            </div>

            <div className="live-view-controls-right">
              {airPlayAvailable && (
                <button type="button" className="live-view-control-btn" onClick={handleAirPlay}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 19V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14" />
                    <polygon points="7 20 12 15 17 20" />
                  </svg>
                </button>
              )}
              <button type="button" className="live-view-control-btn" onClick={handleToggleFullscreen}>
                {isFullscreen ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 9 9 5 5 5" />
                    <polyline points="15 9 19 9 19 5" />
                    <polyline points="15 15 15 19 19 19" />
                    <polyline points="9 15 5 15 5 19" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <polyline points="21 3 14 10" />
                    <polyline points="3 21 10 14" />
                  </svg>
                )}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  )
}

export default LiveStreamView
